import exportFromJSON from "export-from-json";
import React, { useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { usePolicyCommands } from "@devices/components/Commands/hooks";
import { NewCommandDialog } from "@devices/components/Commands/NewCommandDialog";
import { ConfirmDialog } from "@devices/components/shared/index";
import { selectPaneStatus } from "@devices/store";
import { Group } from "@devices/types";
import { Box, DialogContentText, Theme } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { withExpiredWrapper } from "@shared/ExpiredWrapper";

import { ImportGroupPolicyDialog } from "./ImportGroupPolicyDialog";
import PolicyCommandsTable from "./PolicyCommandsTable";

const useStyles = makeStyles()((theme: Theme) => ({
    headerBox: { display: "flex", justifyContent: "space-between", paddingBottom: theme.spacing(2) },
    btnBox: { display: "flex", gap: theme.spacing(1), position: "relative" },
}));

function PolicyTab(group: Group): React.JSX.Element {
    const { t } = useTranslation(["sidepanels", "modals"]);
    const { classes } = useStyles();

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const toggleModal = () => setIsDeleteModalOpen((prev) => !prev);

    const policyCommands = group?.policy || [];
    const [newCommandDialogOpen, setNewCommandDialogOpen] = useReducer((state) => !state, false);
    const [importFileDialogOpen, setImportFileDialogOpen] = useReducer((state) => !state, false);

    const {
        clearPolicyCommands,
        loadingState: { updatePolicyLoading, clearPolicyLoading },
    } = usePolicyCommands();

    const exportPolicy = () => {
        exportFromJSON({
            data: policyCommands ?? [],
            fileName: `policy_groupId_${group?.id}`,
            exportType: exportFromJSON.types.json,
        });
    };

    const onDeletePolicy = async () => {
        await clearPolicyCommands({ groupId: group.id, policy: JSON.stringify([]) });
    };

    const paneStatus = useSelector((state) => selectPaneStatus(state));
    if (paneStatus === "pending") {
        return <PaneElement.Loader />;
    }

    return (
        <PaneElement.Container>
            <Box className={classes.headerBox}>
                <PaneElement.Header>{t("sidepanels:groupPanel.policyTab.groupPolicy")}</PaneElement.Header>
                <Box className={classes.btnBox}>
                    <SecondaryButton
                        onClick={setNewCommandDialogOpen}
                        tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.addPolicyCommand") }}
                    >
                        <i className="fas fa-solid fa-square-plus" />
                    </SecondaryButton>
                    <SecondaryButton
                        disabled={updatePolicyLoading}
                        loading={updatePolicyLoading}
                        onClick={setImportFileDialogOpen}
                        tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.importPolicyCommands") }}
                    >
                        <i className="fas fa-solid fa-file-import" />
                    </SecondaryButton>
                    <SecondaryButton
                        disabled={!policyCommands?.length || updatePolicyLoading}
                        onClick={exportPolicy}
                        tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.exportPolicyCommands") }}
                    >
                        <i className="fas fa-solid fa-file-export" />
                    </SecondaryButton>
                    <DangerOutlinedButton
                        disabled={!policyCommands?.length || clearPolicyLoading}
                        loading={clearPolicyLoading}
                        onClick={() => setIsDeleteModalOpen(true)}
                        tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.clearPolicyCommands") }}
                    >
                        <i className="fas fa-solid fa-trash-alt" />
                    </DangerOutlinedButton>
                </Box>
            </Box>
            {policyCommands?.length ? (
                <PolicyCommandsTable groupId={group.id} policyCommands={policyCommands} />
            ) : (
                <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: 0 }}>
                    <PaneElement.NoData message={t("sidepanels:groupPanel.table.noGroupPolicy")} />
                </Box>
            )}

            <NewCommandDialog
                title={t("modals:newPolicyCommand.title")}
                group={group}
                isOpen={newCommandDialogOpen}
                toggleDialog={setNewCommandDialogOpen}
                isPolicy={true}
            />

            <ImportGroupPolicyDialog
                open={importFileDialogOpen}
                groupId={group.id}
                policyCommands={policyCommands ?? []}
                onClose={setImportFileDialogOpen}
            />
            <ConfirmDialog
                title={t("modals:deletePolicy.title")}
                dangerButton
                primaryActionText={t("modals:deletePolicy.primaryActionText")}
                open={isDeleteModalOpen}
                onConfirm={onDeletePolicy}
                onClose={toggleModal}
            >
                <DialogContentText>{t("modals:deletePolicy.description")}</DialogContentText>
            </ConfirmDialog>
        </PaneElement.Container>
    );
}
export default withExpiredWrapper(PolicyTab);
